import { Component, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { TranslateDirective } from '@ngx-translate/core';
import {
  ButtonComponent,
  ModalCloseDirective,
  ModalCloseReason,
  ModalHeaderButtonsComponent,
  ModalHeaderCloseButtonComponent,
  ModalHeaderComponent,
  ModalHeaderTitleComponent,
  ModalPanelComponent,
  ModalRef,
  PageActionPanelComponent,
  StickyPanelBottomComponent,
  StickyPanelBottomContentComponent
} from '@tdwb/core';
import { Agreement, AgreementsService, AgreementStatus, AgreementType } from '@tdwb/lazy/platform/preferences';
import { AmexAgreementComponent, AmexAgreementFormModel } from './amex-agreement.component';
import { CegAgreementComponent } from './ceg-agreement.component';
import { CmeAgreementComponent } from './cme-agreement.component';
import { IexAgreementComponent } from './iex-agreement.component';
import { MeAgreementComponent } from './me-agreement.component';
import { NasdaqAgreementComponent, NasdaqAgreementFormModel } from './nasdaq-agreement.component';
import { NyseAgreementComponent, NyseAgreementFormModel } from './nyse-agreement.component';
import { OpraAgreementComponent, OpraAgreementFormModel } from './opra-agreement.component';
import { RusselAgreementComponent } from './russel-agreement.component';
import { TdAgreementComponent } from './td-agreement.component';
import { UsotcAgreementComponent } from './usotc-agreement.component';

@Component({
  selector: 'td-wb-current-agreements-modal',
  templateUrl: 'current-agreements.modal.component.html',
  styleUrls: ['current-agreements.modal.component.scss'],
  imports: [
    TranslateDirective,
    ButtonComponent,
    ModalCloseDirective,
    ModalHeaderButtonsComponent,
    ModalHeaderCloseButtonComponent,
    ModalHeaderComponent,
    ModalHeaderTitleComponent,
    ModalPanelComponent,
    PageActionPanelComponent,
    StickyPanelBottomComponent,
    StickyPanelBottomContentComponent,
    AmexAgreementComponent,
    CegAgreementComponent,
    CmeAgreementComponent,
    IexAgreementComponent,
    MeAgreementComponent,
    NasdaqAgreementComponent,
    NyseAgreementComponent,
    OpraAgreementComponent,
    RusselAgreementComponent,
    TdAgreementComponent,
    UsotcAgreementComponent
  ]
})
export class CurrentAgreementsModalComponent implements OnInit {
  readonly agreements = input<Array<Agreement>>([]);
  readonly pagingMode = input(false);
  readonly initialItemsIndex = input<number | undefined>(0);
  protected itemsIndex = 0;

  private readonly amex = viewChild(AmexAgreementComponent);
  private readonly nasdaq = viewChild(NasdaqAgreementComponent);
  private readonly nyse = viewChild(NyseAgreementComponent);
  private readonly opra = viewChild(OpraAgreementComponent);

  protected readonly currentAgreement = signal<Agreement | undefined>(undefined);
  protected readonly itemsCount = signal(0);
  protected readonly pendingCount = signal(0);

  private readonly agreementsService = inject(AgreementsService);
  private readonly modalRef = inject(ModalRef<CurrentAgreementsModalComponent>);

  ngOnInit(): void {
    this.itemsIndex = this.initialItemsIndex() ?? 0;
    const agreements = this.agreements();

    if (agreements.length === 0) {
      this.close();
      return;
    }
    // when paging mode , cycle through
    this.currentAgreement.set(agreements[this.itemsIndex]);
    this.itemsCount.set(agreements.length);
    this.updatePendingCount();
  }

  public declineAgreement(): void {
    this.proceedToNextAgreement();
  }

  public acceptAgreement(): void {
    let model: boolean | NasdaqAgreementFormModel | AmexAgreementFormModel | NyseAgreementFormModel | OpraAgreementFormModel;
    const agreement = this.currentAgreement();
    if (agreement === undefined) {
      return;
    }

    switch (agreement.agreementType) {
      case AgreementType.AMEX:
        model = this.amex()?.trySubmitAndReturnModel();
        break;
      case AgreementType.NASDAQ:
        model = this.nasdaq()?.trySubmitAndReturnModel();
        break;
      case AgreementType.NYSE:
        model = this.nyse()?.trySubmitAndReturnModel();
        break;
      case AgreementType.OPRA:
        model = this.opra()?.trySubmitAndReturnModel();
        break;
      default:
        model = undefined;
        break;
    }
    if (model !== undefined) {
      agreement.agreementStatus = AgreementStatus.ACCEPTED;
      agreement.exchangeSubscriptionData = model;
      this.agreementsService.updateAgreement(agreement).subscribe((): void => {
        this.proceedToNextAgreement();
      });
    }
  }

  public isDisabled(): boolean {
    return this.currentAgreement()?.agreementStatus === 'ACCEPTED';
  }

  public close(): void {
    this.modalRef.close(ModalCloseReason.HEADER_BUTTON);
  }

  pageChange(index: number): void {
    this.itemsIndex = index;
    this.currentAgreement.set(this.agreements()[this.itemsIndex]);
    this.updatePendingCount();
  }

  private updatePendingCount(): void {
    const totalPending = this.agreements().reduce((initialValue: number, agreement: Agreement): number => {
      const unSignedAgreement = agreement.agreementStatus === 'UNSIGNED';
      return unSignedAgreement ? initialValue + 1 : initialValue;
    }, 0);
    this.pendingCount.set(totalPending);
  }

  private proceedToNextAgreement(): void {
    if (this.pagingMode()) {
      this.pendingCount.update((value: number): number => value - 1);
      if (this.pendingCount() > 0) {
        this.itemsIndex++;
      }

      if (this.pendingCount() > 0) {
        this.currentAgreement.set(this.agreements()[this.itemsIndex]);
      }
      if (this.pendingCount() === 0) {
        this.close();
      }
    } else {
      this.close();
    }
  }
}
